import { useEffect, useState } from 'react';
import { useSubClients } from '@/api/hooks';

export interface SubClient { ClientID: string; ClientName: string }

/** Termination reasons in the order shown on the form; values are the Maconomy codes. */
export const TERMINATION_REASON_OPTIONS: { code: number; label: string }[] = [
  { code: 2, label: 'BRC Terminated Services' },
  { code: 1, label: 'Client Never Engaged BRC' },
  { code: 0, label: 'Client No Longer Exists (i.e., Final Return Filed)' },
  { code: 3, label: 'Client Terminated Services' },
  { code: 4, label: 'One Time Project' },
  { code: 5, label: 'Other' },
];

/** "Transition of your Crunchafi Lease Accounting" document (Crunchafi-hosted PDF, link from
 *  Tyler 2026-10-08). Shown as plain text if emptied. */
export const CRUNCHAFI_TRANSITION_DOC_URL =
  'https://2603183.fs1.hubspotusercontent-na1.net/hubfs/2603183/01%20Crunchafi%20Files/Crunchafi%20LA%20Client%20Transitions-Firm%20Clients.pdf';

/**
 * Sub-clients of one selected master: checkboxes, Select All / Deselect All, a filter for
 * long lists, and the new-master dropdown when some sub-clients stay active. The sub
 * chosen as new master stays active, so it is taken off the termination checklist.
 * Renders nothing for a client that is not a master or has no active sub-clients.
 */
export function SubClientPanel({
  master, masterName, selected, onSelected, newMaster, onNewMaster, onLoaded, disabled, selectOnly = false,
}: {
  master: string;
  masterName: string;
  selected: string[];
  onSelected: (ids: string[]) => void;
  newMaster: string;
  onNewMaster: (id: string) => void;
  onLoaded: (subs: SubClient[] | null) => void; // null while loading
  disabled?: boolean;
  /** Mass Change: just pick sub-clients (no termination steps, no new master). */
  selectOnly?: boolean;
}) {
  const q = useSubClients(master);
  const subs = q.data?.subs ?? [];
  const [filter, setFilter] = useState('');

  useEffect(() => { onLoaded(q.data ? subs : null); }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <p className="text-xs text-muted-foreground">Checking {master} for sub-clients…</p>;
  if (!q.data?.isMaster || subs.length === 0) return null;

  const sel = new Set(selected);
  const staying = subs.filter((s) => !sel.has(s.ClientID));
  const nm = staying.some((s) => s.ClientID === newMaster) ? newMaster : '';
  const selectable = subs.filter((s) => s.ClientID !== nm);
  const allOn = selectable.length > 0 && selectable.every((s) => sel.has(s.ClientID));
  const f = filter.trim().toLowerCase();
  const shown = f ? subs.filter((s) => `${s.ClientID} ${s.ClientName}`.toLowerCase().includes(f)) : subs;

  function toggle(id: string) {
    onSelected(sel.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">
            {master} - {masterName} is a master client with {subs.length} active sub-client{subs.length === 1 ? '' : 's'}.
          </p>
          {selectOnly ? (
            <p className="mt-1 text-xs text-muted-foreground">Select the sub-clients to include. Use Select All to include all of them.</p>
          ) : (<div className="mt-1 text-xs text-muted-foreground">
            <p className="font-semibold">How to choose:</p>
            <ol className="ml-4 list-decimal">
              <li>Select the sub-clients being terminated. Use Select All if all of them are.</li>
              <li>If any sub-clients stay active, pick one of them as the New Master Client. It replaces the current
                master and drops off the termination list.</li>
            </ol>
          </div>)}
        </div>
        <button type="button" className="btn-secondary !py-1 text-xs" disabled={disabled}
          onClick={() => onSelected(allOn ? [] : selectable.map((s) => s.ClientID))}>
          {allOn ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      {subs.length > 10 && (
        <input className="input" placeholder="Filter sub-clients…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      )}
      <div className="max-h-56 overflow-auto">
        {shown.map((s) => (
          s.ClientID === nm ? (
            <div key={s.ClientID} className="flex items-center gap-2 py-0.5 text-sm">
              <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border-4 border-primary" />
              {s.ClientID} - {s.ClientName}
              <span className="rounded bg-secondary px-1.5 text-xs font-semibold">New master, stays active</span>
            </div>
          ) : (
            <label key={s.ClientID} className="flex items-center gap-2 py-0.5 text-sm">
              <input type="checkbox" disabled={disabled} checked={sel.has(s.ClientID)} onChange={() => toggle(s.ClientID)} />
              {s.ClientID} - {s.ClientName}
            </label>
          )
        ))}
      </div>
      {!selectOnly && staying.length > 0 && (
        <div className="max-w-md">
          <label className="label">New Master Client * <span className="font-normal text-muted-foreground">({staying.length} sub-client{staying.length === 1 ? '' : 's'} staying active)</span></label>
          <select className="input" value={nm} disabled={disabled} onChange={(e) => onNewMaster(e.target.value)}>
            <option value="">— select —</option>
            {staying.map((s) => <option key={s.ClientID} value={s.ClientID}>{s.ClientID} - {s.ClientName}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
